// Eigenes Plugin (Bridge) im Plugin-Fenster des Mixers.
//   Original: das echte Fenster des Plugins vom Studio-Rechner als laufendes Bild, mit Maus und Tastatur bedienbar.
//             Öffnet sich nur hier (Klick auf das Plugin im Mixer) und schließt mit dem Fenster.
//   Regler:   alle Regler als Liste im SRS-Design (geht auch ohne Bridge, z. B. für andere im Camp).
import { useEffect, useMemo, useRef, useState } from 'react';
import type { InsertSlot } from '@srs/shared';
import { useStudio } from '../state/store.ts';
import { mutInserts } from '../state/mixer.ts';
import { useBridge } from './Dante.tsx';
import { clearEditorCrash, closeEditor, describe, editorCrashed, editorInput, openEditor, prioritizeChannel, textAt, useFx, chainSlotOf } from '../audio/bridgefx.ts';

const STATE_TEXT: Record<string, string> = {
  live: 'Klingt live über die Bridge',
  aktuell: 'Gerechnete Fassung ist aktuell',
  rechnet: 'Bridge rechnet',
  wartet: 'Wird gleich neu gerechnet',
  'ohne-bridge': 'Bridge nicht verbunden: bis zur nächsten Berechnung klingt die Spur ohne dieses Plugin',
  fehlt: 'Plugin ist auf dem verbundenen Studio-Rechner nicht installiert',
  fehler: 'Fehler beim Rechnen',
};

export function BridgePanel({ channel, slot }: { channel: string; slot: InsertSlot }) {
  const bridge = useBridge();
  const status = useFx((s) => s.status[channel]);
  const pluginId = slot.plugin.slice(7);
  const desc = useFx((s) => s.descs[pluginId]);
  const lite = useStudio((s) => s.project.pluginInfo?.[pluginId]);
  const canOriginal = bridge.connected && bridge.paired;
  const [tab, setTab] = useState<'original' | 'regler'>(canOriginal ? 'original' : 'regler');
  useEffect(() => { if (canOriginal) void describe(pluginId); }, [canOriginal, pluginId]);
  // Dieses Plugin zuerst auf dem Studio-Rechner laden
  useEffect(() => { if (canOriginal) prioritizeChannel(channel); }, [canOriginal, channel]);

  return (
    <div className="bfx">
      <div className="bfx-bar">
        <div className="seg">
          <button className={tab === 'original' ? 'on' : ''} disabled={!canOriginal} title={canOriginal ? 'Originalfenster vom Studio-Rechner' : 'Nur mit verbundener Bridge'} onClick={() => setTab('original')}>Original</button>
          <button className={tab === 'regler' ? 'on' : ''} onClick={() => setTab('regler')}>Regler</button>
        </div>
        <span className={`pill ${status?.state === 'live' || status?.state === 'aktuell' ? 'green' : status?.state === 'fehler' || status?.state === 'fehlt' ? 'red' : 'amber'}`} title={status?.msg}>
          {status ? STATE_TEXT[status.state] + (status.state === 'rechnet' && status.percent !== undefined ? ` ${status.percent} %` : '') : 'Bridge'}
        </span>
      </div>
      {slot.unmapped?.length ? <div className="warn" style={{ margin: '0 0 8px' }}>Auto-Mix konnte nicht zuordnen: {slot.unmapped.join(', ')}. Bitte im Plugin einstellen.</div> : null}
      {tab === 'original' && canOriginal
        ? <OriginalView channel={channel} slot={slot} pluginId={pluginId} name={desc?.name ?? lite?.name ?? slot.label ?? 'Plugin'} onRegler={() => setTab('regler')} />
        : <ParamList channel={channel} slot={slot} params={desc?.params.filter((p) => p.auto).map((p) => ({ i: p.i, name: p.name, label: p.label, steps: p.steps })) ?? lite?.params ?? []} desc={desc} />}
    </div>
  );
}

/** Originalfenster als Bild; Maus und Tastatur gehen an das Plugin auf dem Studio-Rechner */
function OriginalView({ channel, slot, pluginId, name, onRegler }: { channel: string; slot: InsertSlot; pluginId: string; name: string; onRegler: () => void }) {
  const [cs, setCs] = useState<{ chainId: string; slot: number } | null>(null);
  const [err, setErr] = useState('');
  const [blocked, setBlocked] = useState(() => editorCrashed(pluginId));
  const frame = useFx((s) => (cs ? s.frames[`${cs.chainId}#${cs.slot}`] : undefined));
  const ready = useFx((s) => s.chainsRev >= 0 && chainSlotOf(channel, slot.id) !== null);
  const imgRef = useRef<HTMLImageElement>(null);
  const down = useRef(-1);

  useEffect(() => {
    let alive = true, opened: { chainId: string; slot: number } | null = null;
    setErr('');
    if (blocked) return;
    void (async () => {
      if (!chainSlotOf(channel, slot.id)) return;   // öffnet, sobald die Kette bereit ist (ready)
      try {
        opened = await openEditor(channel, slot.id, pluginId);
        if (!alive) { if (opened) closeEditor(opened); return; }
        if (!opened) setErr('Plugin wird auf dem Studio-Rechner noch geladen …');
        setCs(opened);
      } catch (e) { setErr((e as Error).message); }
    })();
    return () => { alive = false; if (opened) closeEditor(opened); };
  }, [channel, slot.id, ready, blocked]); // eslint-disable-line react-hooks/exhaustive-deps

  const pos = (e: React.PointerEvent | React.WheelEvent) => {
    const r = imgRef.current!.getBoundingClientRect();
    const w = frame?.w ?? r.width, h = frame?.h ?? r.height;
    return { x: ((e.clientX - r.left) / r.width) * w, y: ((e.clientY - r.top) / r.height) * h };
  };
  const mods = (e: React.MouseEvent | React.KeyboardEvent) => (e.shiftKey ? 1 : 0) | (e.ctrlKey ? 2 : 0) | (e.altKey ? 4 : 0) | (e.metaKey ? 8 : 0);

  if (blocked) return (
    <div className="bfx-wait stack" style={{ alignItems: 'center' }}>
      <span>Beim letzten Öffnen des Fensters von <b>{name}</b> hat sich die Bridge beendet. Das Fenster öffnet sich deshalb nicht mehr von selbst. Einstellen geht über die Regler.</span>
      <div className="row"><button className="btn small primary" onClick={onRegler}>Zu den Reglern</button><button className="btn small" onClick={() => { clearEditorCrash(pluginId); setBlocked(false); }}>Fenster trotzdem öffnen</button></div>
    </div>
  );
  if (!cs) return (
    <div className="bfx-wait stack" style={{ alignItems: 'center' }}>
      <span>{err || (ready ? 'Originalfenster wird geöffnet …' : 'Plugin wird auf dem Studio-Rechner geladen …')}</span>
      {err && <button className="btn small primary" onClick={onRegler}>Zu den Reglern</button>}
    </div>
  );
  return (
    <div className="bfx-orig" tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Escape') return; e.preventDefault(); e.stopPropagation(); editorInput(cs, { kind: 'key', key: e.key, keyDown: true, mods: mods(e) }); }}
      onKeyUp={(e) => { if (e.key === 'Escape') return; e.preventDefault(); e.stopPropagation(); editorInput(cs, { kind: 'key', key: e.key, keyDown: false, mods: mods(e) }); }}>
      {frame ? (
        <img ref={imgRef} src={frame.url} width={frame.w} height={frame.h} alt="Originalfenster des Plugins" draggable={false}
          onPointerDown={(e) => { e.preventDefault(); (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); (e.currentTarget.parentElement as HTMLElement).focus(); down.current = e.button; editorInput(cs, { kind: 'down', ...pos(e), button: e.button, mods: mods(e), dx: e.detail || 1 }); }}
          onPointerMove={(e) => editorInput(cs, { kind: 'move', ...pos(e), button: down.current >= 0 ? down.current + 1 : 0, mods: mods(e) })}
          onPointerUp={(e) => { editorInput(cs, { kind: 'up', ...pos(e), button: e.button, mods: mods(e), dx: e.detail || 1 }); down.current = -1; }}
          onWheel={(e) => editorInput(cs, { kind: 'wheel', ...pos(e), dx: e.deltaX, dy: e.deltaY, mods: mods(e) })}
          onContextMenu={(e) => e.preventDefault()} />
      ) : <div className="bfx-wait">Bild kommt …</div>}
      <div className="t2" style={{ fontSize: 11.5, marginTop: 6 }}>Bedienung wie am Studio-Rechner. Änderungen landen im Song und sind live hörbar.</div>
    </div>
  );
}

/** Reglerliste aus den Parametern des Plugins */
function ParamList({ channel, slot, params, desc }: { channel: string; slot: InsertSlot; params: { i: number; name: string; label: string; steps: number }[]; desc?: ReturnType<typeof useFx.getState>['descs'][string] }) {
  const commit = useStudio((s) => s.commit);
  const [q, setQ] = useState('');
  const [onlySet, setOnlySet] = useState(false);
  const list = useMemo(() => params.filter((p) => (!q || p.name.toLowerCase().includes(q.toLowerCase())) && (!onlySet || slot.params[`p${p.i}`] !== undefined)).slice(0, 200), [params, q, onlySet, slot.params]);
  if (!params.length) return <div className="bfx-wait">Regler sind noch nicht bekannt. Sobald die Bridge verbunden ist, liest das Studio sie aus.</div>;
  const set = (i: number, v: number) => commit(`${slot.label ?? 'Plugin'}: Regler`, (p) => {
    const s = mutInserts(p, channel).find((x) => x.id === slot.id);
    if (!s) return;
    s.params = { ...s.params, [`p${i}`]: v };
    if (s.texts) { const t = { ...s.texts }; delete t[`p${i}`]; s.texts = t; }
    s.ai = false;
  }, { merge: true });
  const valueOf = (i: number) => slot.params[`p${i}`] ?? desc?.params.find((x) => x.i === i)?.value ?? 0;
  return (
    <div className="bfx-params">
      <div className="row" style={{ gap: 8, marginBottom: 8 }}>
        <input className="input" placeholder="Regler suchen" value={q} onChange={(e) => setQ(e.target.value)} style={{ flex: 1 }} />
        <label className="t2" style={{ fontSize: 12, display: 'flex', gap: 6, alignItems: 'center' }}><input type="checkbox" checked={onlySet} onChange={(e) => setOnlySet(e.target.checked)} />nur gesetzte</label>
      </div>
      <div className="bfx-list">
        {list.map((p) => {
          const v = valueOf(p.i);
          const text = slot.texts?.[`p${p.i}`] ?? textAt(desc, p.i, v);
          return (
            <div key={p.i} className="bfx-row">
              <span className="n" title={p.name}>{p.name}</span>
              <input type="range" min={0} max={1} step={p.steps > 1 ? 1 / (p.steps - 1) : 0.001} value={v} onChange={(e) => set(p.i, Number(e.target.value))} />
              <span className="v mono">{text}</span>
            </div>
          );
        })}
      </div>
      {params.length > list.length && <div className="t2" style={{ fontSize: 11.5, marginTop: 6 }}>{params.length - list.length} weitere Regler, bitte suchen.</div>}
    </div>
  );
}
